import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Global function to open cookie preferences
let openCookiePreferencesGlobal: (() => void) | null = null;

export const openCookiePreferences = () => {
  if (openCookiePreferencesGlobal) {
    openCookiePreferencesGlobal();
  }
};

// Toggle Switch Component
const ToggleSwitch = ({
  enabled,
  onChange,
  disabled = false,
  label,
}: {
  enabled: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!enabled);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onChange(!enabled);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        relative inline-flex h-8 w-14 flex-shrink-0 rounded-full border-2 border-transparent 
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2
        ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
        ${enabled ? 'bg-gold' : 'bg-slate-400'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-lg ring-0 
          transition-transform duration-200 ease-in-out
          ${enabled ? 'translate-x-6' : 'translate-x-0'}
        `}
      />
    </button>
  );
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  const openBanner = useCallback(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const saved = JSON.parse(consent);
        setAnalyticsEnabled(saved.analytics === true);
        setMarketingEnabled(saved.marketing === true);
      } catch {
        setAnalyticsEnabled(false);
        setMarketingEnabled(false);
      }
    }
    setShowPreferences(true);
    setShowBanner(true);
  }, []);

  useEffect(() => {
    openCookiePreferencesGlobal = openBanner;
    return () => {
      openCookiePreferencesGlobal = null;
    };
  }, [openBanner]);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setAnalyticsEnabled(true);
    setMarketingEnabled(true);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setAnalyticsEnabled(false);
    setMarketingEnabled(false);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      necessary: true,
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(savedPreferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleAnalyticsChange = (value: boolean) => {
    setAnalyticsEnabled(value);
  };

  const handleMarketingChange = (value: boolean) => {
    setMarketingEnabled(value);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        >
          <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            {!showPreferences ? (
              /* Initial Banner View */
              <div className="p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full">
                        <i className="ri-cookie-line text-xl text-gold"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-navy">Cookie Preferences</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      We use cookies to enhance your browsing experience, analyse site traffic, and personalise content. 
                      By clicking &quot;Accept All&quot;, you consent to our use of cookies in accordance with our{' '}
                      <Link to="/privacy-policy" className="text-gold hover:underline font-medium">
                        Privacy Policy
                      </Link>
                      . You can manage your preferences or reject non-essential cookies.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowPreferences(true)}
                      className="px-5 py-2.5 text-sm font-medium text-navy border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Manage Preferences
                    </button>
                    <button
                      type="button"
                      onClick={handleRejectAll}
                      className="px-5 py-2.5 text-sm font-medium text-navy border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      type="button"
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 text-sm font-semibold text-navy bg-gold rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Preferences Panel View */
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowPreferences(false)}
                      className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-navy transition-colors cursor-pointer"
                      aria-label="Go back"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <h3 className="text-lg font-semibold text-navy">Cookie Preferences</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBanner(false)}
                    className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-navy transition-colors cursor-pointer"
                    aria-label="Close"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies - Always On */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-navy mb-1">Strictly Necessary</h4>
                        <p className="text-xs text-gray-500">
                          Essential for the website to function properly. Cannot be disabled.
                        </p>
                      </div>
                      <ToggleSwitch
                        enabled={true}
                        onChange={() => {}}
                        disabled={true}
                        label="Strictly necessary cookies (always enabled)"
                      />
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-navy mb-1">Analytics</h4>
                        <p className="text-xs text-gray-500">
                          Help us understand how visitors interact with our website.
                        </p>
                      </div>
                      <ToggleSwitch
                        enabled={analyticsEnabled}
                        onChange={handleAnalyticsChange}
                        label="Toggle analytics cookies"
                      />
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-navy mb-1">Marketing</h4>
                        <p className="text-xs text-gray-500">
                          Used to deliver relevant advertisements and track campaign performance.
                        </p>
                      </div>
                      <ToggleSwitch
                        enabled={marketingEnabled}
                        onChange={handleMarketingChange}
                        label="Toggle marketing cookies"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="px-5 py-2.5 text-sm font-medium text-navy border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Reject All
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="px-5 py-2.5 text-sm font-medium text-navy border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Accept All
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePreferences}
                    className="px-6 py-2.5 text-sm font-semibold text-navy bg-gold rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
